import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { AnalysisEdge, AnalysisNode } from "../common/analysis-model";
import { GraphData, Linqset, ORGANISATION_MODEL_KEY } from "../common/linqset-model";
import { createInsightsFilter, getModelsForFilter, InsightsFilter, ModelDetails } from "../common/organisation";
import { MODEL_API, RepositoryService } from "../linq-utils/repository.service";
import { InsightsService } from "../model-services/insights.service";
import { OrganisationService } from "../organisation/organisation.service";

const COMPONENT = "EPM scorecard tab"

// the EPM scorecard as a tab of a model's or a filter's insights. It reads the analysis data for what's being viewed
// (one model, the models a filter picks, or every model for the organisation model) rather than an analysis graph
@Component({
    selector: 'app-epm-scorecard-tab',
    template: `
        @if (loading) {
            <div class="scorecard-tab-status" role="status"><mat-spinner diameter="22"></mat-spinner><span>Building the scorecard…</span></div>
        }@else if (error) {
            <p class="scorecard-tab-status error" role="alert">{{error}}</p>
        }@else if (nodes && !nodes.length) {
            <p class="scorecard-tab-status">There are no actions to score here yet.</p>
        }@else if (nodes && organisationModelData) {
            <app-epm-scorecard [models]="models" [analysisNodes]="nodes" [analysisEdges]="edges" [organisationModelData]="organisationModelData"
                [filter]="filter" [workspaceKey]="linqset.workspaceKey" [workspaceName]="linqset.workspaceName || 'Current state'"
                [active]="!readOnly" [readOnly]="readOnly" [refreshTrigger]="trigger" (stepSelected)="stepSelected.emit($event)" (valuesApplied)="load()"></app-epm-scorecard>
        }
    `,
    styles: [`
        .scorecard-tab-status { display: flex; align-items: center; gap: 10px; margin: 16px; color: var(--slate, #5b6172); }
        .error { color: var(--red, #d8443b); }
    `],
    standalone: false
})
export class ScorecardTabComponent implements OnChanges {
    @Input() linqset:Linqset
    @Input() trigger:any                                   // the insights were refreshed
    @Input() readOnly = false                              // for viewing only: no AI decisions or links away to edit
    @Output() stepSelected = new EventEmitter<string>()    // the key of a step chosen on the scorecard

    nodes:AnalysisNode[]|undefined
    edges:AnalysisEdge[] = []
    organisationModelData:GraphData|undefined
    models:ModelDetails[] = []
    filter:InsightsFilter|undefined
    loading = false
    error = ""

    private request = 0

    constructor(private organisationService:OrganisationService, private insightsService:InsightsService, private repoService:RepositoryService) {}

    ngOnChanges(changes:SimpleChanges) {
        if (changes['linqset'] || changes['trigger']) this.load()
    }

    // reads the analysis data again, also when the scorecard has set output values and had the insights rescored
    async load() {
        const organisation = this.organisationService.organisation
        const linqset = this.linqset
        if (!organisation || !linqset) return

        const request = ++this.request
        this.loading = true
        this.error = ""
        try {
            const workspaceKey = linqset.workspaceKey
            const project = organisation.projects?.find(p => p._key === workspaceKey)
            // a filter's models, one model, or (for the organisation model) all of them
            const filter:InsightsFilter = linqset.isFilterModel() && linqset.filter
                ? { ...linqset.filter, workspaceKey }
                : { ...createInsightsFilter(workspaceKey), modelKeys: linqset.isProcessModel() ? [linqset.modelKey] : [] }
            const [data, organisationModelData] = await Promise.all([
                this.insightsService.loadAnalysisGraphForFilter(organisation, filter),
                this.insightsService.afterRefresh(workspaceKey, () => this.repoService.callSingleAPI(this.repoService.getData<GraphData>(MODEL_API(organisation._key, ORGANISATION_MODEL_KEY, workspaceKey)), COMPONENT, "Load the organisation model")),
            ])
            if (request !== this.request) return

            const models = getModelsForFilter(filter, organisation, project)
            const modelKeys = models.map(m => m._key)
            this.filter = filter
            this.models = models
            this.organisationModelData = organisationModelData
            this.nodes = (data?.nodes || []).filter(n => modelKeys.includes(n.modelKey))
            this.edges = (data?.edges || []).filter(e => modelKeys.includes(e.modelKey))
        }catch(err) {
            if (request !== this.request) return
            console.error("Unable to build the EPM scorecard", err)
            this.error = "The scorecard couldn't be built. Please try again."
        }finally {
            if (request === this.request) this.loading = false
        }
    }
}
